import { useMutation } from '@apollo/client';
import { useAtomValue } from 'jotai';

import {
  PAYMENT_GATEWAY,
  TEST_RECORD_PAYMENT_TOKEN,
  TEST_RECORD_SUCCEEDED_STATUS,
} from '../../constants';
import type { Country } from '../../entities/Country';
import type { Currency } from '../../entities/Currency';
import type { Environment, UseCase } from '../../entities/enums';
import { CREATE_2C2P_PAYMENT_INTENT } from '../../graphql';
import { CREATE_2C2P_INVOICE_PAYMENT_INTENT } from '../../graphql/Mutations';
import type {
  HirerPaymentResult,
  ICreate2C2PPaymentIntentForInvoicePayload,
  ICreate2C2PPaymentIntentPayload,
  Invoice,
  PaymentStatus,
} from '../../types';
import {
  redirectCartidQuery,
  redirectPayPalQuery,
} from '../../utils/constants';
import getReturnUrl from '../../utils/getReturnUrl';
import { PaymentDefaultError } from '../Errors/customErrors';
import { hidePayPalAtom } from '../Maintenance/MaintenanceStore';

interface InvoiceInputs {
  customerId: string;
  accountId: string;
  prn: string;
  invoice: Invoice[];
  email: string;
  totalForPaymentIntent: number;
}

interface CajaInputs {
  cartId: string;
  referenceId: string;
}

export interface UsePayPalInput<T> {
  useCase: UseCase;
  currency: Currency;
  country: Country;
  env: Environment;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

const usePayPal = ({
  useCase,
  currency,
  env,
  invoiceInputs = {
    customerId: '',
    accountId: '',
    prn: '',
    invoice: [],
    email: '',
    totalForPaymentIntent: 0,
  },
  cajaInputs = {
    cartId: '',
    referenceId: '',
  },
}: UsePayPalInput<UseCase>) => {
  const { customerId, accountId, prn, invoice, email, totalForPaymentIntent } =
    invoiceInputs;

  const { cartId, referenceId } = cajaInputs;

  const [create2C2PPaymentIntent] = useMutation(CREATE_2C2P_PAYMENT_INTENT);
  const [create2C2PPaymentIntentForInvoices] = useMutation(
    CREATE_2C2P_INVOICE_PAYMENT_INTENT,
  );
  const isMaintenance = useAtomValue(hidePayPalAtom);

  const submitPayPalPayment = async (): Promise<HirerPaymentResult> => {
    // TODO: Tracking

    if (currency !== 'HKD') {
      return { status: 'requires_action' };
    }

    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY['2c2p'],
      [redirectPayPalQuery, redirectCartidQuery(cartId)],
      useCase,
      env,
    );

    const inputVariables: ICreate2C2PPaymentIntentPayload = {
      orderId: referenceId,
      paymentMethodType: 'DPAY',
      frontendReturnUrl,
      channelCode: 'PAYPAL',
    };

    const paymentIntent = await create2C2PPaymentIntent({
      variables: {
        input: inputVariables,
      },
    });

    const paymentIntentResponse = paymentIntent.data?.create2C2PPaymentIntent;
    const paymentLink = paymentIntentResponse?.paymentLink;
    const paymentToken = paymentIntentResponse?.paymentToken;

    // Scalability: skip payment gateway call
    if (paymentToken === TEST_RECORD_PAYMENT_TOKEN) {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
      };
    }

    const eWalletData = {
      paymentLink,
      paymentToken,
    };

    localStorage.setItem('processingPaymentData', JSON.stringify(eWalletData));

    if (!paymentLink) {
      throw new PaymentDefaultError(paymentIntentResponse?.message);
    }

    window.location.assign(paymentIntentResponse.paymentLink);

    return { status: 'succeeded' };
  };

  const submitPayPalPaymentForInvoices =
    async (): Promise<HirerPaymentResult> => {
      if (currency !== 'HKD') {
        return { status: 'requires_action' };
      }

      if (isMaintenance) {
        throw new PaymentDefaultError('Payment method is in maintenance');
      }

      const frontendReturnUrl = getReturnUrl(
        PAYMENT_GATEWAY['2c2p'],
        [redirectPayPalQuery],
        useCase,
        env,
      );

      const invoices = invoice.map(({ id, ...rest }) => ({
        reference: id,
        ...rest,
      }));

      const inputVariables: ICreate2C2PPaymentIntentForInvoicePayload = {
        seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
        seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
        paymentMethodType: 'DPAY',
        channelCode: 'PAYPAL',
        totalForPaymentIntent,
        invoices,
        email,
        prn,
        frontendReturnUrl,
      };

      const paymentIntent = await create2C2PPaymentIntentForInvoices({
        variables: {
          input: inputVariables,
        },
      });

      const paymentIntentResponse =
        paymentIntent.data?.create2C2PPaymentIntentForInvoices;
      const paymentLink = paymentIntentResponse?.paymentLink;
      const paymentToken = paymentIntentResponse?.paymentToken;

      // Scalability: skip payment gateway call
      if (paymentToken === TEST_RECORD_PAYMENT_TOKEN) {
        return {
          status: 'succeeded' as PaymentStatus,
          paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
        };
      }

      const eWalletData = {
        paymentLink,
        paymentToken,
      };

      localStorage.setItem(
        'processingPaymentData',
        JSON.stringify(eWalletData),
      );

      if (!paymentLink) {
        throw new PaymentDefaultError(paymentIntentResponse?.message);
      }

      window.location.assign(paymentIntentResponse.paymentLink);

      return {
        status: 'succeeded' as PaymentStatus,
      };
    };

  return {
    submitPayPalPayment,
    submitPayPalPaymentForInvoices,
  };
};

export default usePayPal;
